/* ============================================================================
 * ATELIER EDGE-TINT - product-card letterbox bands painted as an extension of
 * the photo's own edge pixels, so non-square images read as the photo
 * continuing instead of blank white. Orientation-aware: a landscape photo's
 * bands continue its top/bottom ROWS; a portrait photo's bands continue its
 * left/right COLUMNS (keeping the wall/floor line at the photo's own height).
 *
 * LAYERING (Chris-approved, 2026-08-18): the et-bg underlay <img> (a 64px
 * rendition stretched + CSS-blurred, emitted in the card MARKUP so it arrives
 * with the page - never a white band) shows first; these accurate strips
 * OVERTAKE it - same-frame from cache, a brief crossfade only on the first
 * ever sight of an image.
 *
 * CUT-OUT GUARD (2026-08-27): a transparent-background image (PNG/WebP
 * product cut-out) gets NEITHER layer. Its blurred underlay showed straight
 * through the product's clear surround as a glow in the product's own
 * colour, and its edge strips sampled transparent so nothing ever covered
 * it - a permanent halo on every cut-out product (Chris caught it). The
 * sampler now reads the outer ring's alpha; a see-through ring = cut-out =
 * underlay hidden, no strips (the box's own white IS the right band). The
 * underlay is only ever revealed AFTER an opaque verdict (or when no verdict
 * is possible), so a cold first sight never shows the glow either.
 *
 * STUDIO EDGE (2026-08-27, same day): a white-background product shot
 * cropped at the product (ADP Soul Groove: tap base on the bottom row) had
 * that product STRETCHED into the band. An edge line whose median colour is
 * white/near-white is a studio background: its band is that flat colour and
 * the product touching it is ignored. Lifestyle edges (median darker) are
 * untouched. When both bands are studio-white the blurred underlay is
 * skipped too - nothing to raster. Net cheaper than before. See STUDIO_MIN.
 *
 * PERF CONTRACT (the reason this file looks the way it does):
 *  - NEVER touches the card's main <img> - no crossorigin, no attribute
 *    writes, no fetch-mode change. LCP images and preloads are unaffected.
 *  - Samples the SAME width=64 rendition the et-bg underlay displays (shared
 *    URL + CORS mode = one ~3KB download serves both), low priority + async
 *    decode, scheduled at idle. One sample per unique image per SESSION -
 *    strips persist in localStorage, so repeat views paint synchronously at
 *    script-run (same frame as a cached image, zero network, zero sampling).
 *  - Paints via a CSS variable + .is-tinted; adaptive entrance (.et-0 =
 *    instant when the tint lands before the photo is on screen). Scroll-time
 *    cost is zero - bands are plain backgrounds, no filters, no live layers.
 *
 * CONSUMERS: any .pc-im (shared product card - collections, home rails,
 * /search, range rails, wishlist grid) and .at-spc-im (header palette).
 * ::before rules live in atelier-collection.css (pc) and atelier-header.css
 * (palette). Collections re-tint after AJAX facet renders via the
 * #ProductGridContainer observer; JS-rendered surfaces also call
 * ATEdgeTint.scan(root) after building cards (safe to call any time).
 * ============================================================================ */
(function () {
  'use strict';
  var cache = {};       /* probeUrl -> {ar,t,b,l,r} strips, or false = failed */
  var pending = {};     /* probeUrl -> [boxes awaiting the in-flight probe] */
  var SEL = '.pc-im, .at-spc-im';

  /* strips persist in localStorage (~1KB/image) so REPEAT views paint the tint
     synchronously at script-run - the same frame as a cached image, no probe.
     Failures (false) are session-only. Storage errors are silently ignored. */
  var LS_KEY = 'at-edge-tint-v5', LS_CAP = 150, saveT = null;   /* v5: studio-edge verdicts (v4 cut-outs, v3 64px probe) */
  /* STUDIO EDGE (2026-08-27): an edge line whose MEDIAN colour is white or
     near-white (every channel >= STUDIO_MIN) is a studio background - its
     band becomes that flat colour, so a product cropped at that edge (the
     Soul Groove tap base) is ignored instead of being stretched into the
     band. Calibrated on 61 catalogue images: studio edges have medians of
     254-255, lifestyle edges 228 or lower - a clean gap, and the median
     stays white however much product touches the edge. Lifestyle edges are
     untouched: they stretch exactly as before. */
  var STUDIO_MIN = 240;
  try { cache = JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch (e) { cache = {}; }
  try { localStorage.removeItem('at-edge-tint-v3'); localStorage.removeItem('at-edge-tint-v4'); } catch (e) { /* older verdicts - flush */ }
  function persist() {
    clearTimeout(saveT);
    saveT = setTimeout(function () {
      try {
        var keep = {}, keys = Object.keys(cache), n = 0;
        for (var i = 0; i < keys.length && n < LS_CAP; i++) {
          if (cache[keys[i]]) { keep[keys[i]] = cache[keys[i]]; n++; }
        }
        localStorage.setItem(LS_KEY, JSON.stringify(keep));
      } catch (e) { /* quota/privacy mode: memory cache still works */ }
    }, 400);
  }

  /* the tiny sampling rendition - the SAME URL the et-bg underlay displays.
     TWO URL generations exist: Algolia records carry legacy cdn.shopify.com
     URLs (palette + /search cards); Liquid's image_url emits store-routed
     /cdn/shop/ paths (collections + all snippet cards) - same-origin, so the
     CORS canvas read is trivially safe there. Rejecting the second form was
     the "/collections never gets strips" bug. */
  function probeUrl(src) {
    if (!src) return null;
    try {
      var u = new URL(src, location.href);
      if (u.hostname.indexOf('cdn.shopify.com') === -1 &&
          u.pathname.indexOf('/cdn/shop/') === -1) return null;
      u.searchParams.set('width', '64');
      u.searchParams.delete('height');
      return u.href;
    } catch (e) { return null; }
  }

  function sample(im) {
    /* NATIVE-resolution sampling (no extra squash): the edge slice is 1/64 of
       the photo - a wide product cannot tint the band (the "wood sneaking
       into the wall" bug a 16x16 downscale caused). */
    var W = Math.max(2, Math.min(96, im.naturalWidth || 64));
    var H = Math.max(2, Math.min(96, im.naturalHeight || 64));
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    var ctx = c.getContext('2d'); ctx.drawImage(im, 0, 0, W, H);
    var px = ctx.getImageData(0, 0, W, H).data;
    /* CUT-OUT: any see-through pixel on the outer ring = transparent
       background. No strips (they would sample transparent = paint nothing)
       and the underlay stays hidden - see the header. */
    if (ringHasAlpha(px, W, H)) return { alpha: 1 };
    function strip(sx, sy, sw, sh) {
      var s = document.createElement('canvas'); s.width = sw; s.height = sh;
      s.getContext('2d').drawImage(c, sx, sy, sw, sh, 0, 0, sw, sh);
      return 'url(' + s.toDataURL() + ')';
    }
    var x, y, oT = [], oB = [], oL = [], oR = [];
    for (x = 0; x < W; x++) { oT.push(x * 4); oB.push(((H - 1) * W + x) * 4); }
    for (y = 0; y < H; y++) { oL.push(y * W * 4); oR.push((y * W + W - 1) * 4); }
    var pT = plain(px, oT), pB = plain(px, oB), pL = plain(px, oL), pR = plain(px, oR);
    return {
      ar: im.naturalWidth / (im.naturalHeight || 1),
      t: pT || strip(0, 0, W, 1), b: pB || strip(0, H - 1, W, 1),
      l: pL || strip(0, 0, 1, H), r: pR || strip(W - 1, 0, 1, H),
      pt: !!pT, pb: !!pB, pl: !!pL, pr: !!pR
    };
  }

  /* flat band for a studio (white) edge line, or null = stretch it as before */
  function plain(px, offs) {
    var n = offs.length, rs = [], gs = [], bs = [], i, o;
    for (i = 0; i < n; i++) { o = offs[i]; rs.push(px[o]); gs.push(px[o + 1]); bs.push(px[o + 2]); }
    function med(a) { a.sort(function (p, q) { return p - q; }); return a[n >> 1]; }
    var r = med(rs), g = med(gs), b = med(bs);
    if (r < STUDIO_MIN || g < STUDIO_MIN || b < STUDIO_MIN) return null;
    var c = 'rgb(' + r + ',' + g + ',' + b + ')';
    return 'linear-gradient(' + c + ',' + c + ')';
  }

  function ringHasAlpha(px, W, H) {
    var x, y;
    function clear(x, y) { return px[(y * W + x) * 4 + 3] < 250; }
    for (x = 0; x < W; x++) if (clear(x, 0) || clear(x, H - 1)) return true;
    for (y = 0; y < H; y++) if (clear(0, y) || clear(W - 1, y)) return true;
    return false;
  }

  /* et-bg underlay entrance - ADAPTIVE: already loaded (cache) = instant,
     arriving late (cold first visit) = soft fade, never a harsh pop. Only
     called once the image is KNOWN opaque (strip verdict) or when no verdict
     is possible (probe failed) - a cut-out's underlay must never show. */
  function reveal(box) {
    var bgim = box.querySelector('.et-bg');
    if (!bgim || bgim.dataset.in) return;
    bgim.dataset.in = '1';
    if (bgim.complete) bgim.classList.add('et-now', 'et-in');
    else bgim.addEventListener('load', function () { this.classList.add('et-in'); });
  }
  function hideUnderlay(box) {
    var bgim = box.querySelector('.et-bg');
    if (!bgim) return;
    bgim.dataset.in = '1';                       /* reveal() is now a no-op */
    bgim.classList.remove('et-in', 'et-now');
    bgim.style.display = 'none';                 /* inline - no theme CSS edit needed */
  }

  /* constant strips, halves meeting behind the photo - no fade inside a band */
  function bg(d, land) {
    return land
      ? d.t + ' top / 100% 51% no-repeat, ' + d.b + ' bottom / 100% 51% no-repeat'
      : d.l + ' left / 51% 100% no-repeat, ' + d.r + ' right / 51% 100% no-repeat';
  }

  /* instant (no transition) when the tint lands BEFORE the photo is on screen
     (pre-first-paint sync scan, or the image hasn't finished loading yet);
     the short fade only softens a genuinely LATE tint over a visible photo. */
  var retryT = null;
  function queueRetry() {
    clearTimeout(retryT);
    retryT = setTimeout(function () { scan(document); }, 180);
  }
  function paint(box, d, sync) {
    if (d.alpha) { hideUnderlay(box); return; }  /* cut-out: white bands are right, no strips */
    var r = box.getBoundingClientRect();
    if (!r.width || !r.height) {
      /* box not laid out yet (early sync scan vs a server-rendered grid still
         behind its skeleton): UNMARK + retry. Without this the card stayed
         marked-but-unpainted forever - the recurring "/collections looks
         different from /search" bug (JS-built cards never hit this path). */
      var tries = +(box.dataset.etTries || 0) + 1;
      if (tries < 6) { box.dataset.etTries = tries; delete box.dataset.et; queueRetry(); }
      return;
    }
    /* instant (et-0) ONLY on the pre-first-paint sync pass - the box is not on
       screen yet so instant is truly invisible. Every ASYNC arrival fades: the
       card's white bands are already visible while the photo loads, so "tint
       beat the photo" still means "tint lands on a visible card" - painting it
       instantly there was the cold-load white->snap Chris kept seeing. */
    var land = d.ar >= r.width / r.height;
    /* both bands studio-white = flat colour bands; the blurred underlay
       would add nothing but a raster - skip it. Lifestyle shots keep the
       underlay + stretched strips exactly as before. */
    if (land ? (d.pt && d.pb) : (d.pl && d.pr)) hideUnderlay(box); else reveal(box);
    if (sync) box.classList.add('et-0');
    box.style.setProperty('--edge-tint', bg(d, land));
    box.classList.add('is-tinted');
  }

  function load(purl, box, sync) {
    if (cache[purl] === false) { reveal(box); return; }
    if (cache[purl]) { paint(box, cache[purl], sync); return; }
    if (pending[purl]) { pending[purl].push(box); return; }
    pending[purl] = [box];
    var im = new Image();
    im.crossOrigin = 'anonymous';
    try { im.fetchPriority = 'low'; } catch (e) { /* older browsers */ }
    im.decoding = 'async';
    function fail() {
      var boxes = pending[purl] || []; delete pending[purl];
      cache[purl] = false;
      for (var i = 0; i < boxes.length; i++) reveal(boxes[i]);
    }
    im.onload = function () {
      var d;
      try { d = sample(im); } catch (e) { fail(); return; }
      var boxes = pending[purl] || []; delete pending[purl];
      cache[purl] = d;
      for (var i = 0; i < boxes.length; i++) paint(boxes[i], d, false);
      persist();
    };
    im.onerror = fail;
    im.src = purl;
  }

  function scan(root, sync) {
    var boxes = (root && root.querySelectorAll ? root : document).querySelectorAll(SEL);
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      /* the et-bg underlay is revealed by paint()/reveal() once the strip
         verdict is in - never here, or a cut-out would glow until sampled */
      if (box.dataset.et) continue;
      var img = box.querySelector('img:not(.et-bg)');
      var purl = probeUrl(img && (img.currentSrc || img.src));
      if (!purl) continue;              /* placeholder / non-CDN: leave for a later scan */
      box.dataset.et = '1';
      load(purl, box, !!sync);
    }
  }

  /* idle with a timeout floor - on a busy loading page rIC can starve; the
     timeout keeps tints near the images without ever preempting layout work */
  var idle = window.requestIdleCallback
    ? function (f) { window.requestIdleCallback(f, { timeout: 1500 }); }
    : function (f) { setTimeout(f, 200); };
  function scanIdle(root) { idle(function () { scan(root); }); }

  /* collections: AJAX facet renders swap #ProductGridContainer - re-tint after */
  function watchGrid() {
    var grid = document.getElementById('ProductGridContainer');
    if (!grid || !window.MutationObserver) return;
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t); t = setTimeout(function () { scanIdle(grid); }, 120);
    }).observe(grid, { childList: true, subtree: true });
  }

  /* SYNCHRONOUS first scan at script-run (deferred = DOM parsed): cached
     strips paint pre-first-render; uncached ones start their probes now.
     The window-load re-scan is the safety net for anything the early pass
     could not lay hands on (paint() also self-retries zero-size boxes). */
  scan(document, true);
  watchGrid();
  window.addEventListener('load', function () { scanIdle(document); });

  /* probe exposed so card builders can reuse the SAME width=64 URL as the
     et-bg underlay (shared cache entry - one download serves display+sampling) */
  window.ATEdgeTint = { scan: scanIdle, probe: probeUrl };
})();
